import type {
  GradeElementaryMiddle,
  GradeMiddle,
  Revision,
  SchoolType,
  Semester,
} from '~/@entities/SchoolSystem/schema.ts'

import { SCHOOL_TYPE_KO_ABBR, SCHOOL_TYPE_KO_FULL } from '../SchoolSystem/constants'
import type { CurriculumApi } from './api'
import {
  CURRICULUM_KEYS,
  NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS,
  PRE_OPEN_CURRICULUM_KEYS,
  REVISION_KO,
} from './constant'

const _utils = {
  numberToGradeSubject(grade: number) {
    if (grade < 1 || grade > 6) {
      throw new Error('학년은 1부터 6 사이여야 합니다.')
    }
    return String(grade) as GradeElementaryMiddle
  },
}

export namespace Entity {
  export class Curriculum {
    key: CurriculumApi.Response.CurriculumInfo['key']
    type: CurriculumApi.Response.CurriculumInfo['type']
    id: CurriculumApi.Response.CurriculumInfo['id']
    name: CurriculumApi.Response.CurriculumInfo['name']
    url: CurriculumApi.Response.CurriculumInfo['url']
    hidden: CurriculumApi.Response.CurriculumInfo['hidden']
    revisionId: CurriculumApi.Response.CurriculumInfo['revisionId']
    revisionName: CurriculumApi.Response.CurriculumInfo['revisionName']
    schoolId: CurriculumApi.Response.CurriculumInfo['schoolId']
    schoolName: CurriculumApi.Response.CurriculumInfo['schoolName']
    gradeId: CurriculumApi.Response.CurriculumInfo['gradeId']
    gradeName: CurriculumApi.Response.CurriculumInfo['gradeName']
    semesterId: CurriculumApi.Response.CurriculumInfo['semesterId']
    semesterName: CurriculumApi.Response.CurriculumInfo['semesterName']
    sequence: CurriculumApi.Response.CurriculumInfo['sequence']
    subjectId: CurriculumApi.Response.CurriculumInfo['subjectId']
    subjectName: CurriculumApi.Response.CurriculumInfo['subjectName']
    bigChapterId: CurriculumApi.Response.CurriculumInfo['bigChapterId']
    bigChapterName: CurriculumApi.Response.CurriculumInfo['bigChapterName']
    middleChapterId: CurriculumApi.Response.CurriculumInfo['middleChapterId']
    middleChapterName: CurriculumApi.Response.CurriculumInfo['middleChapterName']
    littleChapterId: CurriculumApi.Response.CurriculumInfo['littleChapterId']
    littleChapterName: CurriculumApi.Response.CurriculumInfo['littleChapterName']

    constructor(params: CurriculumApi.Response.CurriculumInfo) {
      this.key = params.key
      this.type = params.type
      this.id = params.id
      this.name = params.name
      this.url = params.url
      this.hidden = params.hidden
      this.revisionId = params.revisionId
      this.revisionName = params.revisionName
      this.schoolId = params.schoolId
      this.schoolName = params.schoolName
      this.sequence = params.sequence
      this.gradeId = params.gradeId
      this.gradeName = params.gradeName
      this.semesterId = params.semesterId
      this.semesterName = params.semesterName
      this.subjectId = params.subjectId
      this.subjectName = params.subjectName
      this.bigChapterId = params.bigChapterId
      this.bigChapterName = params.bigChapterName
      this.middleChapterId = params.middleChapterId
      this.middleChapterName = params.middleChapterName
      this.littleChapterId = params.littleChapterId
      this.littleChapterName = params.littleChapterName
    }
  }

  export type TrieKey = CurriculumApi.TrieKey
  type SubscribeStatus = 'SUBSCRIBED' | 'PART_SUBSCRIBED' | 'UNSUBSCRIBED'
  type CurriculumStatusType =
    | Extract<CurriculumApi.CurriculumStatus, 'ACTIVE' | 'HIDDEN'>
    | 'PRE_OPEN' // 프리오픈
    | 'NOT_READY' // 컨텐츠가 준비되지 않음 등으로 지원하지 않음 = 지원예정
  export class SchoolItem<T extends 'ALL_TYPE' | SchoolType = SchoolType> {
    label: string
    labelFull: string
    value: T extends 'ALL_TYPE' ? 'ALL' : T
    revisions: Revision[]
    trieKeys: TrieKey[]
    /** open 여부 */
    private _status: CurriculumApi.CurriculumStatus = 'ACTIVE'
    /** 구독 여부 */
    private _subscribeStatus: SubscribeStatus = 'SUBSCRIBED'
    constructor(params: Pick<SchoolItem<T>, 'value' | 'trieKeys' | 'revisions'>) {
      this.value = params.value
      this.trieKeys = params.trieKeys
      this.revisions = params.revisions
      this.label = this._setDefaultLabel().label
      this.labelFull = this._setDefaultLabel().labelFull
    }

    private _setDefaultLabel() {
      if (this.value === 'ALL') {
        return { label: '전체', labelFull: '전체' }
      }
      return {
        label: SCHOOL_TYPE_KO_ABBR[this.value as SchoolType],
        labelFull: SCHOOL_TYPE_KO_FULL[this.value as SchoolType],
      }
    }

    static MakeAllTypeItem() {
      const allTypeItem = new SchoolItem<'ALL_TYPE'>({
        value: 'ALL',
        revisions: ['CURRICULUM_15', 'CURRICULUM_22'],
        trieKeys: ['ALL'],
      })
      return allTypeItem
    }

    get status() {
      return this._status
    }
    set status(value: CurriculumApi.CurriculumStatus) {
      this._status = value
    }
    get subscribeStatus() {
      return this._subscribeStatus
    }
    set subscribeStatus(value: SubscribeStatus) {
      this._subscribeStatus = value
    }
  }

  // TODO_EC: 다른 곳에서 BaseCurriculumCoreItem<T> 이렇게 해도 잘 추론 되도록 수정해야함
  interface _BaseCurriculumCoreItem<
    T extends 'ALL_TYPE' | SchoolType = SchoolType,
    U extends 'UNION_GRADE' | 'UNIQUE' = 'UNIQUE',
  > {
    isAllType: T extends 'ALL_TYPE' ? true : false
    trieKey: T extends 'ALL_TYPE' ? 'ALL' : Entity.TrieKey
    revision: T extends 'ALL_TYPE'
      ? undefined
      : U extends 'UNION_GRADE'
        ? CurriculumApi.Revision | undefined
        : CurriculumApi.Revision
    schoolType: T extends 'ALL_TYPE' ? null | SchoolType : SchoolType
    gradeSubject: T extends 'ALL_TYPE'
      ? undefined
      : U extends 'UNION_GRADE'
        ? T extends 'ELEMENTARY'
          ? GradeElementaryMiddle
          : GradeMiddle
        : T extends 'HIGH'
          ? CurriculumApi.HighSchoolSubject
          : GradeElementaryMiddle
    semester: T extends 'ALL_TYPE' ? null : Semester | null
    status: CurriculumStatusType
    subscribeStatus: SubscribeStatus

    lowerTrieKeys: T extends 'ALL_TYPE' ? ['ALL'] : Entity.TrieKey[]
    upperTrieKey: T extends 'ALL_TYPE' ? 'ALL' : Entity.TrieKey
    label: string
    labelShort: string
    labelWithoutSchoolType: string
    labelWithSpace: string
    categorizedGrade: T extends 'ALL_TYPE' ? undefined : GradeElementaryMiddle
  }
  export class AllTypeCurriculumCoreItem<
    T extends null | SchoolType = SchoolType,
  > implements _BaseCurriculumCoreItem<'ALL_TYPE'> {
    readonly isAllType: _BaseCurriculumCoreItem<'ALL_TYPE'>['isAllType'] = true
    readonly trieKey: _BaseCurriculumCoreItem<'ALL_TYPE'>['trieKey'] = 'ALL'
    readonly revision: _BaseCurriculumCoreItem<'ALL_TYPE'>['revision'] = undefined
    readonly schoolType: _BaseCurriculumCoreItem<'ALL_TYPE'>['schoolType']
    readonly gradeSubject: _BaseCurriculumCoreItem<'ALL_TYPE'>['gradeSubject'] = undefined
    readonly semester: _BaseCurriculumCoreItem<'ALL_TYPE'>['semester'] = null
    private _status: _BaseCurriculumCoreItem<'ALL_TYPE'>['status'] = 'ACTIVE'
    private _subscribeStatus: _BaseCurriculumCoreItem<'ALL_TYPE'>['subscribeStatus'] = 'SUBSCRIBED'
    label: _BaseCurriculumCoreItem<'ALL_TYPE'>['label'] = '전체'
    labelShort: _BaseCurriculumCoreItem<'ALL_TYPE'>['labelShort'] = '전체'
    labelWithoutSchoolType: _BaseCurriculumCoreItem<'ALL_TYPE'>['labelWithoutSchoolType'] = '전체'
    labelWithSpace: _BaseCurriculumCoreItem<'ALL_TYPE'>['labelWithSpace'] = '전체'
    readonly categorizedGrade: _BaseCurriculumCoreItem<'ALL_TYPE'>['categorizedGrade'] = undefined
    readonly lowerTrieKeys: _BaseCurriculumCoreItem<'ALL_TYPE'>['lowerTrieKeys'] = ['ALL']
    readonly upperTrieKey: _BaseCurriculumCoreItem<'ALL_TYPE'>['upperTrieKey'] = 'ALL'
    constructor({
      schoolType,
      label,
    }: {
      schoolType: AllTypeCurriculumCoreItem<T>['schoolType']
      label?: string
    }) {
      this.schoolType = schoolType || null
      if (!label) {
        if (schoolType === 'HIGH') {
          this._setCustomLabels('과목 전체')
        } else {
          this._setCustomLabels('학년·학기')
        }
      } else {
        this._setCustomLabels(label)
      }
    }

    private _setCustomLabels(label: string) {
      this.label = label
      this.labelShort = label
      this.labelWithoutSchoolType = label
      this.labelWithSpace = label
    }
    get status() {
      return this._status
    }
    set status(value: CurriculumStatusType) {
      this._status = value
    }
    get subscribeStatus() {
      return this._subscribeStatus
    }
    set subscribeStatus(value: SubscribeStatus) {
      this._subscribeStatus = value
    }
  }

  export class CurriculumCoreItem<
    T extends SchoolType = SchoolType,
  > implements _BaseCurriculumCoreItem<SchoolType, 'UNIQUE'> {
    readonly isAllType: _BaseCurriculumCoreItem<SchoolType>['isAllType']
    readonly id: CurriculumApi.CurriculumCoreDefaultInfo['id']
    readonly trieKey: _BaseCurriculumCoreItem<SchoolType>['trieKey']
    /** 상위 trieKey */
    readonly upperTrieKey: _BaseCurriculumCoreItem<SchoolType>['upperTrieKey']
    /** 하위 trieKey 값들(마지막일 경우 자기 자신) */
    private _lowerTrieKeys: _BaseCurriculumCoreItem<SchoolType>['lowerTrieKeys']
    readonly revision: _BaseCurriculumCoreItem<SchoolType>['revision']
    readonly schoolType: T
    readonly gradeSubject: _BaseCurriculumCoreItem<SchoolType>['gradeSubject']
    readonly semester: _BaseCurriculumCoreItem<SchoolType>['semester']
    readonly orderingNumber?: number
    /** ex) 초1(개정), 초1-1(개정), 고등수학(상)(개정) */
    label: _BaseCurriculumCoreItem<SchoolType>['label']
    /** ex) 초1(개정), 초1-1(개정), 수학(상)(개정) */
    labelShort: _BaseCurriculumCoreItem<SchoolType>['labelShort']
    /** ex) 1 - 1(개정), 1(개정), 수학(상)(개정) */
    labelWithoutSchoolType: _BaseCurriculumCoreItem<SchoolType>['labelWithoutSchoolType']
    /** ex) 초 1 - 1(개정), 1(개정) 고등수학(상)(개정) */
    labelWithSpace: _BaseCurriculumCoreItem<SchoolType>['labelWithSpace']

    /** 고등학교의 경우에는 학년이 없지만 내부적으로 분류 된 값 */
    readonly categorizedGrade: _BaseCurriculumCoreItem<SchoolType>['categorizedGrade']
    /** 해당 커리큘럼의 open 여부 */
    private _status: _BaseCurriculumCoreItem<SchoolType>['status']
    /** 해당 커리큘럼의 구독 여부 */
    private _subscribeStatus: _BaseCurriculumCoreItem<SchoolType>['subscribeStatus'] = 'SUBSCRIBED'

    constructor(
      params: Omit<CurriculumCoreItem<T>, 'isAllType'> & {
        subscribeStatus?: SubscribeStatus
        lowerTrieKeys?: Entity.TrieKey[]
      },
    ) {
      this.isAllType = false
      this.id = params.id
      this.trieKey = params.trieKey
      this.upperTrieKey = params.upperTrieKey
      this._lowerTrieKeys = params?.lowerTrieKeys || []
      this.revision = params.revision
      this.schoolType = params.schoolType
      this.gradeSubject = params.gradeSubject
      this.semester = params.semester
      this._status = params.status
      this._subscribeStatus = params?.subscribeStatus || 'SUBSCRIBED'
      this.orderingNumber = params.orderingNumber

      this.label = params.label
      this.labelShort = params.labelShort
      this.labelWithoutSchoolType = params.labelWithoutSchoolType
      this.labelWithSpace = params.labelWithSpace

      this.categorizedGrade = params.categorizedGrade
    }

    get lowerTrieKeys() {
      return Array.from(new Set(this._lowerTrieKeys))
    }
    set lowerTrieKeys(value: Entity.TrieKey[]) {
      this._lowerTrieKeys = value
    }
    get status() {
      return this._status
    }
    set status(value: CurriculumStatusType) {
      this._status = value
    }
    get subscribeStatus() {
      return this._subscribeStatus
    }
    set subscribeStatus(value: SubscribeStatus) {
      this._subscribeStatus = value
    }

    static From<T extends SchoolType>(
      curriculumCoreItem: CurriculumCoreItem<T>,
    ): CurriculumCoreItem<T> {
      return new CurriculumCoreItem(curriculumCoreItem)
    }
  }

  // 개정이 다른 같은 학년/학기(고등은 함꼐 묶이는 과목)를 학년or학년학기 기반으로 변경 초1, 초1-1, 중1, 중1-1 (고등학교도 학년으로 치환됨 => 고1, 고2, 고3)
  export class UnionGradeCurriculumCoreItem<
    T extends SchoolType = SchoolType,
  > implements _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'> {
    static readonly UNION_KEY = '_UNION_'
    readonly isAllType: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['isAllType'] = false
    readonly trieKey: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['trieKey']
    /** 상위 trieKey */
    readonly upperTrieKey: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['upperTrieKey']
    /** 하위 trieKey 값들(마지막일 경우 자기 자신) */
    private _lowerTrieKeys: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['lowerTrieKeys']
    readonly revision: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['revision'] = undefined
    readonly schoolType: T
    readonly gradeSubject: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['gradeSubject']
    readonly semester: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['semester']

    /** ex) 초등학교1, 초등학교1-1, 고등학교1 */ // => koGradeSemester 이름이 이게 더 맞나..?
    label: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['label']
    /** ex) 초1, 초1-1, 고1 */ // => label
    labelShort: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['labelShort']
    /** ex) 1 - 1, 1 */
    labelWithoutSchoolType: _BaseCurriculumCoreItem<T, 'UNION_GRADE'>['labelWithoutSchoolType']
    /** ex) 초 1 - 1 고 1 */
    labelWithSpace: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['labelWithSpace']

    /** 내부적으로 분류 된 값 */
    readonly categorizedGrade: _BaseCurriculumCoreItem<
      SchoolType,
      'UNION_GRADE'
    >['categorizedGrade']
    /** 해당 커리큘럼의 open 여부 */
    private _status: _BaseCurriculumCoreItem<SchoolType, 'UNION_GRADE'>['status']
    /** 해당 커리큘럼의 구독 여부 */
    private _subscribeStatus: _BaseCurriculumCoreItem<
      SchoolType,
      'UNION_GRADE'
    >['subscribeStatus'] = 'SUBSCRIBED'

    constructor(
      params: Omit<UnionGradeCurriculumCoreItem<T>, 'isAllType' | 'revision'> & {
        subscribeStatus?: SubscribeStatus
        lowerTrieKeys?: Entity.TrieKey[]
      },
    ) {
      this.trieKey = params.trieKey
      this.upperTrieKey = params.upperTrieKey
      this._lowerTrieKeys = params?.lowerTrieKeys || []
      this.schoolType = params.schoolType
      this.gradeSubject = params.gradeSubject
      this.semester = params.semester

      this.label = params.label
      this.labelShort = params.labelShort
      this.labelWithoutSchoolType = params.labelWithoutSchoolType
      this.labelWithSpace = params.labelWithSpace

      this.categorizedGrade = params.categorizedGrade
      this._status = params.status
      this._subscribeStatus = params?.subscribeStatus || 'SUBSCRIBED'
    }

    get lowerTrieKeys() {
      return Array.from(new Set(this._lowerTrieKeys))
    }
    set lowerTrieKeys(value: Entity.TrieKey[]) {
      this._lowerTrieKeys = value
    }
    get status() {
      return this._status
    }
    set status(value: CurriculumStatusType) {
      this._status = value
    }
    get subscribeStatus() {
      return this._subscribeStatus
    }
    set subscribeStatus(value: SubscribeStatus) {
      this._subscribeStatus = value
    }

    static From<T extends SchoolType>(
      unionGradeCurriculumCoreItem: UnionGradeCurriculumCoreItem<T>,
    ): UnionGradeCurriculumCoreItem<T> {
      return new UnionGradeCurriculumCoreItem(unionGradeCurriculumCoreItem)
    }
  }

  export class CurriculumCore {
    readonly revisions: CurriculumApi.Response.CurriculumCore['revisions']
    readonly schoolTypes: CurriculumApi.Response.CurriculumCore['schoolTypes']
    schools: SchoolItem[] = []
    readonly grades: CurriculumCoreItem<'ELEMENTARY' | 'MIDDLE'>[]
    readonly semesters: CurriculumCoreItem<'ELEMENTARY' | 'MIDDLE'>[]
    readonly subjects: CurriculumCoreItem<'HIGH'>[]

    constructor(params: CurriculumApi.Response.CurriculumCore) {
      this.revisions = params.revisions
      this.schoolTypes = params.schoolTypes

      // schoolTypes 기반 schools set
      this.schools = Object.values(
        this.schoolTypes.reduce(
          (acc, { schoolType, trieKey, revision, status }) => {
            if (!acc[schoolType]) {
              acc[schoolType] = new SchoolItem({
                value: schoolType,
                trieKeys: [trieKey],
                revisions: [revision],
              })
              acc[schoolType].status = status
            } else {
              acc[schoolType].trieKeys.push(trieKey)
              acc[schoolType].revisions.push(revision)
              if (status === 'ACTIVE') {
                acc[schoolType].status = 'ACTIVE'
              }
            }
            return acc
          },
          {} as Record<string, SchoolItem>,
        ),
      )
      //  grade 기반 gradeSubjects set
      this.grades = params.grades
        .sort((a, b) => {
          const result = a.grade - b.grade
          if (result !== 0) return result
          if (a.revision === 'CURRICULUM_22' && b.revision !== 'CURRICULUM_22') return -1
          if (a.revision !== 'CURRICULUM_22' && b.revision === 'CURRICULUM_22') return 1
          return 0
        })
        .map((grade) => {
          return this._makeCurriculumCoreItem<'ELEMENTARY' | 'MIDDLE'>({
            id: grade.id,
            schoolType: grade.schoolType,
            trieKey: grade.trieKey,
            status: grade.status,
            revision: grade.revision,
            subject: null,
            grade: grade.grade,
            semester: null,
          })
        })

      this.semesters = params.semesters
        .sort((a, b) => a.orderingNumber - b.orderingNumber)
        .map((semester) => {
          const item = this._makeCurriculumCoreItem<'ELEMENTARY' | 'MIDDLE'>({
            id: semester.id,
            schoolType: semester.schoolType,
            trieKey: semester.trieKey,
            status: semester.status,
            revision: semester.revision,
            subject: null,
            grade: semester.grade,
            semester: semester.semester,
            orderingNumber: semester.orderingNumber,
          })

          this._setGradeLowerTrieKeys(item)

          return item
        })

      this.subjects = params.subjects
        .sort((a, b) => a.orderingNumber - b.orderingNumber)
        .map((subject) => {
          return this._makeCurriculumCoreItem<'HIGH'>({
            id: subject.id,
            schoolType: subject.schoolType,
            trieKey: subject.trieKey,
            status: subject.status,
            revision: subject.revision,
            subject: subject.subject,
            grade: null,
            semester: null,
            orderingNumber: subject.orderingNumber,
          })
        })
    }

    private _setGradeLowerTrieKeys(semester: CurriculumCoreItem<'ELEMENTARY' | 'MIDDLE'>) {
      const _upperItem = this.grades.find(
        (grade) =>
          grade.gradeSubject === semester.gradeSubject &&
          grade.revision === semester.revision &&
          grade.schoolType === semester.schoolType,
      )
      if (!_upperItem) return
      _upperItem.lowerTrieKeys = [..._upperItem.lowerTrieKeys, semester.trieKey]
    }

    private _makeCurriculumCoreItem<T extends SchoolType = SchoolType>(curriculum: {
      id: CurriculumApi.CurriculumCoreDefaultInfo['id']
      trieKey: CurriculumApi.TrieKey
      orderingNumber?: number
      schoolType: T
      revision: CurriculumApi.Revision
      grade: T extends 'HIGH' ? null : CurriculumApi.Grade
      subject: T extends 'HIGH' ? CurriculumApi.HighSchoolSubject : null
      semester: Semester | null
      status: CurriculumApi.CurriculumStatus
    }) {
      const gradeSubject = (() => {
        if (curriculum.subject) {
          return curriculum.subject
        } else if (curriculum.grade) {
          return _utils.numberToGradeSubject(curriculum.grade)
        } else {
          throw new Error('subject 또는 grade가 필요합니다.')
        }
      })()

      const lowerTrieKeys =
        curriculum.schoolType === 'HIGH'
          ? [curriculum.trieKey]
          : curriculum.semester
            ? [curriculum.trieKey]
            : []
      const upperTrieKey = curriculum.trieKey.split('.')
        ? curriculum.trieKey.split('.').slice(0, -1).join('.')
        : curriculum.trieKey

      const _status = (() => {
        if (Object.values(NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS).includes(curriculum.trieKey)) {
          return 'HIDDEN'
        }
        switch (curriculum.status) {
          case 'ACTIVE':
            return 'ACTIVE'
          case 'DRAFT':
          case 'INACTIVE':
          case 'HIDDEN':
            return 'HIDDEN'
          default:
            throw new Error('Invalid curriculum status')
        }
      })()

      const _defaultLabels = () => {
        const _revision =
          curriculum.revision && curriculum.revision !== 'CURRICULUM_15'
            ? `(${REVISION_KO[curriculum.revision]})`
            : ''

        if (curriculum.schoolType === 'HIGH') {
          const _label = `${gradeSubject}${_revision}`
          // 과목명은 /curriculums/core API의 subject 값을 그대로 사용한다.
          // (trieKey 기반 상수 맵에 의존하면 백엔드에 과목이 추가될 때마다 상수를 등록해야 하고, 누락 시 'undefined'가 노출됨)
          const _shortLabel = `${gradeSubject}${_revision}`

          return {
            label: _label,
            labelShort: _shortLabel,
            labelWithoutSchoolType: _label,
            labelWithSpace: _label,
          }
        } else {
          const _schoolKo = SCHOOL_TYPE_KO_ABBR[curriculum.schoolType]
          const _label = `${_schoolKo}${gradeSubject}${curriculum.semester ? `-${curriculum.semester}` : ''}${_revision}`
          const _labelWithoutSchoolType = `${gradeSubject}${curriculum.semester ? ` - ${curriculum.semester}` : ''}${_revision}`
          return {
            label: _label,
            labelShort: _label,
            labelWithoutSchoolType: _labelWithoutSchoolType,
            labelWithSpace: `${_schoolKo} ${_labelWithoutSchoolType}`,
          }
        }
      }

      const _categorizedGrade = (() => {
        if (curriculum.grade) return _utils.numberToGradeSubject(curriculum.grade)

        if (curriculum.schoolType === 'HIGH') {
          switch (curriculum.trieKey) {
            case CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_통합과학1_22개정:
            case CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_통합과학2_22개정: {
              return _utils.numberToGradeSubject(1)
            }
            default: {
              return _utils.numberToGradeSubject(2)
            }
          }
        }
        throw new Error('grade 또는 subject가 필요합니다.')
      })()

      return new CurriculumCoreItem<T>({
        id: curriculum.id,
        orderingNumber: curriculum.orderingNumber,
        trieKey: curriculum.trieKey,
        upperTrieKey,
        lowerTrieKeys,
        revision: curriculum.revision,
        schoolType: curriculum.schoolType,
        gradeSubject,
        semester: curriculum.semester,

        status: _status,
        subscribeStatus: 'SUBSCRIBED',

        label: _defaultLabels().label,
        labelShort: _defaultLabels().labelShort,
        labelWithoutSchoolType: _defaultLabels().labelWithoutSchoolType,
        labelWithSpace: _defaultLabels().labelWithSpace,

        categorizedGrade: _categorizedGrade,
      })
    }

    static makeUnionGradeCurriculumCoreItem<T extends SchoolType>(
      curriculums: CurriculumCoreItem<T>[],
    ) {
      const schoolType = curriculums[0].schoolType

      const _statusPriority: CurriculumCoreItem['status'][] = [
        'HIDDEN',
        'PRE_OPEN',
        'NOT_READY',
        'ACTIVE',
      ]

      const gradeSubject = (() => {
        if (curriculums[0].schoolType === 'HIGH') {
          return curriculums[0].categorizedGrade
        } else {
          return curriculums[0].gradeSubject as UnionGradeCurriculumCoreItem<T>['gradeSubject']
        }
      })()

      const semester = (() => {
        if (curriculums[0].schoolType === 'HIGH') {
          return null
        } else {
          return curriculums[0].semester
        }
      })()

      const _defaultLabels = () => {
        const _schoolKo = SCHOOL_TYPE_KO_ABBR[schoolType]
        const _schoolKoFull = SCHOOL_TYPE_KO_FULL[schoolType]
        if (schoolType === 'HIGH') {
          const _label = `${_schoolKoFull}${gradeSubject}`
          const _labelShort = `${_schoolKo}${gradeSubject}`

          return {
            label: _label,
            labelShort: _labelShort,
            labelWithoutSchoolType: `${gradeSubject}`,
            labelWithSpace: `${_schoolKo} ${gradeSubject}`,
          }
        } else {
          const _label = `${_schoolKoFull}${gradeSubject}${semester ? `-${semester}` : ''}`
          const _labelShort = `${_schoolKo}${gradeSubject}${semester ? `-${semester}` : ''}`
          const _labelWithoutSchoolType = `${gradeSubject}${semester ? ` - ${semester}` : ''}`
          return {
            label: _label,
            labelShort: _labelShort,
            labelWithoutSchoolType: _labelWithoutSchoolType,
            labelWithSpace: `${_schoolKo} ${_labelWithoutSchoolType}`,
          }
        }
      }
      return new UnionGradeCurriculumCoreItem<T>({
        trieKey: curriculums
          .map(({ trieKey }) => trieKey)
          .join(UnionGradeCurriculumCoreItem.UNION_KEY),
        upperTrieKey: curriculums
          .map(({ upperTrieKey }) => upperTrieKey)
          .join(UnionGradeCurriculumCoreItem.UNION_KEY),
        lowerTrieKeys: curriculums.flatMap(({ lowerTrieKeys }) => lowerTrieKeys),
        schoolType,
        gradeSubject,
        semester,
        categorizedGrade: gradeSubject,
        label: _defaultLabels().label,
        labelShort: _defaultLabels().labelShort,
        labelWithoutSchoolType: _defaultLabels().labelWithoutSchoolType,
        labelWithSpace: _defaultLabels().labelWithSpace,
        status: _statusPriority.find((s) => curriculums.some((t) => t.status === s)) ?? 'ACTIVE',
        subscribeStatus: 'SUBSCRIBED',
      })
    }

    static makeMapFromArray(curriculumCoreItems: CurriculumCoreItem<SchoolType>[]) {
      return new Map(curriculumCoreItems.map((item) => [item.trieKey, item]))
    }

    static removeRevisionKoFromLabel(label: string) {
      return label.replace(/\(.*?개정\)/g, '')
    }
  }
}
